import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProperties } from "../lib/api";
import { SearchBox } from "../components/SearchBox";
import { PropertyCard } from "../components/PropertyCard";
import { ShieldCheck, HandshakeIcon, Layers, MessageSquare, ArrowRight, Award, Phone } from "lucide-react";
import { COMPANY, telLink, waLink } from "../lib/config";

const FEATURES = [
  { icon: ShieldCheck, title: "Güvenilir Hizmet", text: "Müşterilerimizin ihtiyaçlarını ön planda tutuyor, şeffaf ve dürüst bir yaklaşımla çalışıyoruz." },
  { icon: Award, title: "Profesyonel Danışmanlık", text: "31+ yıllık tecrübemizle alım, satım ve kiralama süreçlerinde profesyonel destek sağlıyoruz." },
  { icon: Layers, title: "Geniş İlan Seçenekleri", text: "Daire, villa, arsa ve işyeri kategorilerinde farklı ihtiyaçlara uygun seçenekler." },
  { icon: MessageSquare, title: "Hızlı İletişim", text: "Sorularınız için WhatsApp ve telefon üzerinden anında ulaşabilirsiniz." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    fetchProperties({ featured: true, limit: 6 }).then(setFeatured).catch(() => setFeatured([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[78vh] flex flex-col justify-end pt-32 pb-8 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1670589953882-b94c9cb380f5?w=2000&q=80"
            alt="SANTRAL EMLAK LTD"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/65 via-slate-900/35 to-slate-900/70" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex-1 flex items-center">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-semibold tracking-widest uppercase mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C0562F]" /> {COMPANY.tagline}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white mb-6">
              Hayalinizdeki Gayrimenkulü <em className="text-[#F3B893] not-italic">Birlikte</em> Bulalım
            </h1>
            <p className="text-lg text-slate-200 max-w-2xl mb-8 leading-relaxed">
              Türkiye’nin tüm il ve ilçelerinde satılık ve kiralık gayrimenkul seçenekleri. SANTRAL EMLAK LTD ile güvenli ve profesyonel bir deneyim yaşayın.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/ilanlar?islem=satilik" data-testid="hero-satilik-btn" className="btn-accent px-7 py-3.5 rounded-full font-semibold text-sm inline-flex items-center gap-2">Satılık İlanlar <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/ilanlar?islem=kiralik" data-testid="hero-kiralik-btn" className="px-7 py-3.5 rounded-full font-semibold text-sm bg-white/10 backdrop-blur border border-white/25 text-white hover:bg-white/20 transition-colors">Kiralık İlanlar</Link>
            </div>
          </div>
        </div>

        {/* Compact search bar sitting cleanly at bottom of hero */}
        <div className="relative max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 mt-10">
          <SearchBox />
        </div>
      </section>

      {/* FEATURED LISTINGS */}
      <section className="pt-16 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <div className="label-caps mb-3">Portfolio</div>
              <h2 className="font-display text-3xl sm:text-4xl text-slate-900">Öne Çıkan İlanlar</h2>
            </div>
            <Link to="/ilanlar" data-testid="see-all-listings" className="text-sm font-semibold text-[#C0562F] hover:text-[#A04726] inline-flex items-center gap-2">Tüm İlanları Gör <ArrowRight className="w-4 h-4" /></Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {featured.slice(0, 6).map((p, i) => <PropertyCard key={p.id} p={p} idx={i} />)}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="label-caps mb-3">Neden Biz</div>
            <h2 className="font-display text-3xl sm:text-4xl text-slate-900 mb-4">Neden SANTRAL EMLAK?</h2>
            <p className="text-slate-600 leading-relaxed">1995’ten bu yana kesintisiz sürdürdüğümüz hizmet anlayışımız, binlerce ailenin yuvasına, işyerine ve yatırımına ulaşmasına aracılık etti.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} data-testid={`feature-${i}`} className="p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:border-[#C0562F]/30 hover:bg-white transition-colors">
                <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center mb-6"><f.icon className="w-5 h-5 text-white" /></div>
                <h3 className="font-display text-xl text-slate-900 mb-3">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1686771416282-3888ddaf249b?w=1200" alt="Hakkımızda" className="rounded-2xl w-full object-cover aspect-[4/5]" />
            <div className="absolute -bottom-8 -right-4 lg:-right-8 bg-white p-6 rounded-2xl shadow-xl border border-slate-100 max-w-[220px]">
              <div className="font-display text-4xl text-slate-900">31+</div>
              <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold mt-1">Yıl Tecrübe</div>
              <div className="text-sm text-slate-600 mt-3">1995’ten bugüne kesintisiz güvenilir hizmet.</div>
            </div>
          </div>

          <div>
            <div className="label-caps mb-3">Hakkımızda</div>
            <h2 className="font-display text-3xl sm:text-4xl text-slate-900 mb-6 leading-tight">Müşteri Odaklı, Profesyonel Emlak Hizmeti</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              SANTRAL EMLAK LTD olarak müşterilerimizin ihtiyaçlarına uygun gayrimenkulleri bulmalarına yardımcı oluyor,
              alım, satım ve kiralama süreçlerinde güvenilir ve profesyonel hizmet sunuyoruz.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              Bursa merkezli ofisimizden Türkiye’nin tüm il ve ilçelerine hizmet veriyor; her müşterimizin sürecini bir aile
              projesi gibi ele alıyoruz. Yetki Belgemiz: <span className="font-semibold text-slate-900">{COMPANY.license}</span>
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/hakkimizda" data-testid="about-more" className="btn-primary px-6 py-3 rounded-full text-sm font-semibold">Daha Fazla Bilgi</Link>
              <a href={telLink()} className="px-6 py-3 rounded-full text-sm font-semibold border border-slate-300 hover:border-slate-900 transition-colors inline-flex items-center gap-2"><Phone className="w-4 h-4" /> {COMPANY.phone}</a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-slate-900 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-5xl text-white mb-6 leading-tight">Aradığınız Gayrimenkulü Birlikte Bulalım</h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-10">31 yılı aşkın tecrübemizle sizin için doğru gayrimenkulü bulmak üzere buradayız.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/ilanlar" data-testid="cta-browse" className="btn-accent px-8 py-4 rounded-full font-semibold">İlanları İncele</Link>
            <Link to="/iletisim" data-testid="cta-contact" className="px-8 py-4 rounded-full font-semibold bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors">Bize Ulaşın</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
